import type { CartProductLocalWithPrices } from '~/types/local-types'
import type { ExtraOrderBuddy, ItemOrderBuddy, Maybe, ProductOrderBuddy } from '~/types/orderbuddy-types'
import { shop } from '~/store/shop'
import { DispatchTypeOrderBuddy } from '~/types/orderbuddy-types'

const allProductsPickup = computed<ProductOrderBuddy[]>(() =>
    shop().pickupMenu?.flatMap(({ products }) => products) || []
)

const allProductsDelivery = computed<ProductOrderBuddy[]>(() =>
    shop().deliveryMenu?.flatMap(({ products }) => products) || []
)

const allExtrasPickup = computed<ExtraOrderBuddy[]>(
    () => allProductsPickup.value?.flatMap(({ extras }) => extras) || []
)

const allExtrasDelivery = computed<ExtraOrderBuddy[]>(
    () => allProductsDelivery.value?.flatMap(({ extras }) => extras) || []
)

const allExtraItemsPickup = computed<ItemOrderBuddy[]>(
    () => allExtrasPickup.value?.flatMap(({ items }) => items) || []
)

const allExtraItemsDelivery = computed<ItemOrderBuddy[]>(
    () => allExtrasDelivery.value?.flatMap(({ items }) => items) || []
)

function findProductPickup (productId: string | number): Maybe<ProductOrderBuddy> {
    return allProductsPickup.value?.find(({ id }) => id === (typeof productId === 'string' ? Number.parseInt(productId) : productId)) || null
}

function findProductDelivery (productId: string | number): Maybe<ProductOrderBuddy> {
    return allProductsDelivery.value?.find(({ id }) => id === (typeof productId === 'string' ? Number.parseInt(productId) : productId)) || null
}

function findProduct (productId: string | number, dispatchType: DispatchTypeOrderBuddy): Maybe<ProductOrderBuddy> {
    return dispatchType === DispatchTypeOrderBuddy.DELIVER ? findProductDelivery(productId) : findProductPickup(productId)
}

function findExtraPickup (extraId: string | number): Maybe<ExtraOrderBuddy> {
    return allExtrasPickup.value?.find(({ id }) => id === (typeof extraId === 'string' ? Number.parseInt(extraId) : extraId)) || null
}

function findExtraDelivery (extraId: string | number): Maybe<ExtraOrderBuddy> {
    return allExtrasDelivery.value?.find(({ id }) => id === (typeof extraId === 'string' ? Number.parseInt(extraId) : extraId)) || null
}

function findExtra (extraId: string | number, dispatchType: DispatchTypeOrderBuddy): Maybe<ExtraOrderBuddy> {
    return dispatchType === DispatchTypeOrderBuddy.DELIVER ? findExtraDelivery(extraId) : findExtraPickup(extraId)
}

function findExtraItemPickup (extraItemId: string | number): Maybe<ItemOrderBuddy> {
    return allExtraItemsPickup.value?.find(({ id }) => id === extraItemId) || null
}

function findExtraItemDelivery (extraItemId: string | number): Maybe<ItemOrderBuddy> {
    return allExtraItemsDelivery.value?.find(({ id }) => id === extraItemId) || null
}

function findExtraItem (extraId: string | number, dispatchType: DispatchTypeOrderBuddy): Maybe<ItemOrderBuddy> {
    return dispatchType === DispatchTypeOrderBuddy.DELIVER ? findExtraItemDelivery(extraId) : findExtraItemPickup(extraId)
}

export function productCanBeInCart (cartProduct: CartProductLocalWithPrices, dispatchType: DispatchTypeOrderBuddy): boolean {
    if (!findProduct(cartProduct.id, dispatchType)) {
        return false
    } else {
        if (cartProduct.extras) {
            const extrasInCart = Object.keys(cartProduct.extras)
            for (const extraId of extrasInCart) {
                if (!findExtra(extraId, dispatchType)) {
                    return false
                } else {
                    const extraItemsInCart = Object.keys(cartProduct.extras[extraId].items)
                    for (const extraItemId of extraItemsInCart) {
                        if (!findExtraItem(extraItemId, dispatchType)) {
                            return false
                        }
                    }
                }
            }
        }
    }
    return true
}
